import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
// Removed incorrect imports: Select, Input, Textarea, Checkbox, Modal
import { Button, ConfirmationModal } from '../common'; // Keep Button, add ConfirmationModal for potential use
import { toast } from 'react-toastify';

// --- PERMISSION_SCHEMA including Prospector module ---
const PERMISSION_SCHEMA = {
  leads: {
    label: 'Leads',
    permissions: {
      access: { label: 'Enable Leads Access', type: 'boolean' },
      create: { label: 'Create New Leads', type: 'boolean' },
      read: { label: 'View Leads', type: 'select', options: ['none', 'own', 'all'] }, // Represents read + viewAll
      update: { label: 'Update Leads', type: 'select', options: ['none', 'own', 'all'] }, // Represents update + updateOwnOnly
      delete: { label: 'Delete Leads', type: 'select', options: ['none', 'own', 'all'] }, // Represents delete + deleteOwnOnly
      assign: { label: 'Assign Leads to Others', type: 'boolean' },
      export: { label: 'Export Leads', type: 'select', options: ['none', 'own', 'all'] }, // Represents export + exportOwnOnly
      segments_access: { label: 'Segments Access', type: 'select', options: ['view_only', 'create_delete_own', 'create_for_all', 'full_access'] }
    }
  },
  deals: {
    label: 'Deals',
    permissions: {
      access: { label: 'Enable Deals Access', type: 'boolean' },
      create: { label: 'Create New Deals', type: 'boolean' },
      read: { label: 'View Deals', type: 'select', options: ['none', 'own', 'all'] },
      update: { label: 'Update Deals', type: 'select', options: ['none', 'own', 'all'] },
      delete: { label: 'Delete Deals', type: 'select', options: ['none', 'own', 'all'] },
      assign: { label: 'Assign Deals to Others', type: 'boolean' },
      export: { label: 'Export Deals', type: 'select', options: ['none', 'own', 'all'] }
    }
  },
  calendar: {
    label: 'Calendar',
    permissions: {
      access: { label: 'Enable Calendar Access', type: 'boolean' },
      create: { label: 'Create New Events', type: 'boolean' },
      read: { label: 'View Events', type: 'select', options: ['none', 'own', 'all'] },
      update: { label: 'Update Events', type: 'select', options: ['none', 'own', 'all'] },
      delete: { label: 'Delete Events', type: 'select', options: ['none', 'own', 'all'] },
      complete: { label: 'Complete Events', type: 'select', options: ['none', 'own', 'all'] }, // Represents complete + completeOwnOnly
      email: { label: 'Send Emails to Enrolled Students', type: 'boolean' }
    }
  },
  tasks: {
    label: 'Tasks',
    permissions: {
      access: { label: 'Enable Tasks Access', type: 'boolean' },
      create: { label: 'Create New Tasks', type: 'boolean' },
      read: { label: 'View Tasks', type: 'select', options: ['none', 'own', 'all'] },
      update: { label: 'Update Tasks', type: 'select', options: ['none', 'own', 'all'] },
      delete: { label: 'Delete Tasks', type: 'select', options: ['none', 'own', 'all'] },
      assignAll: { label: 'Assign Tasks to Others', type: 'boolean' } // Simplified from assignAll
    }
  },
  employees: {
    label: 'Employees',
    permissions: {
      access: { label: 'Enable Employees Access', type: 'boolean' },
      create: { label: 'Create New Employees', type: 'boolean' },
      read: { label: 'View Employees', type: 'select', options: ['none', 'own', 'all'] },
      update: { label: 'Update Employees', type: 'select', options: ['none', 'own', 'all'] },
      delete: { label: 'Delete Employees', type: 'select', options: ['none', 'own', 'all'] },
      viewLogs: { label: 'View Logs', type: 'boolean' },
      manageRoles: { label: 'Manage Roles & Permissions', type: 'boolean' },
      viewInvoices: { label: 'View Tutor Invoices', type: 'boolean' }, // Assuming this is a general permission now
      updateInvoiceStatus: { label: 'Update Tutor Invoice Status', type: 'boolean' }, // Added
      deleteInvoices: { label: 'Delete Tutor Invoices', type: 'boolean' } // Added
    }
  },
  kpis: {
    label: 'KPIs',
    permissions: {
      access: { label: 'Enable KPIs Access', type: 'boolean' }
    }
  },
  mailer: {
    label: 'Email Marketing',
    permissions: {
      access: { label: 'Enable Email Marketing Access', type: 'boolean' }
    }
  },
  chat: {
    label: 'Team Chat',
    permissions: {
      access: { label: 'Enable Team Chat Access', type: 'boolean' }
    }
  },
  // Add Prospector module permissions
  prospector: {
    label: 'Prospector',
    permissions: {
      access: { label: 'Enable Prospector Access', type: 'boolean' },
      create: { label: 'Create New Prospecting Lists', type: 'boolean' },
      read: { label: 'View Prospector Data', type: 'select', options: ['none', 'own', 'all'] },
      update: { label: 'Update Prospector Data', type: 'select', options: ['none', 'own', 'all'] },
      delete: { label: 'Delete Prospector Items', type: 'select', options: ['none', 'own', 'all'] }
    }
  },
  settings: {
    label: 'Settings',
    permissions: {
      access: { label: 'Enable Settings Access', type: 'boolean' },
      company: { label: 'Company Settings', type: 'boolean' },
      currencies: { label: 'Currency Settings', type: 'boolean' },
      courses: { label: 'Course Settings', type: 'boolean' },
      pipelineStages: { label: 'Pipeline Stages Settings', type: 'boolean' },
      reminders: { label: 'Reminder Settings', type: 'boolean' },
      emailProfiles: { label: 'Email Profile Settings', type: 'boolean' },
      emailTemplates: { label: 'Email Template Settings', type: 'boolean' },
      emailLogs: { label: 'View Email Logs', type: 'boolean' },
      permissions: { label: 'Manage Permissions (this tab)', type: 'boolean' },
      integrations: { label: 'Integration Settings', type: 'boolean' },
      automation: { label: 'Automation Settings', type: 'boolean' },
      bulkEmail: { label: 'Bulk Email (SES) Settings', type: 'boolean' }
    }
  },
};

// --- Helper functions update ---
// Updated getSelectValue to read the 'value' property directly if available
const getSelectValue = (perms, keyPrefix) => {
    // Check the new structure first: category[key].value
    if (perms[keyPrefix]?.value) {
        return perms[keyPrefix].value;
    }
    // Fallback to old boolean logic if value property doesn't exist
    if (!perms[keyPrefix]?.enabled) return 'none'; // Check enabled flag
    if (perms[`${keyPrefix}OwnOnly`]?.enabled) return 'own';
    if (perms[`viewAll`]?.enabled && keyPrefix === 'read') return 'all';
    if (perms[`assignAll`]?.enabled && keyPrefix === 'assign') return 'all';
    // If enabled but no specific 'own' or 'all' flag, assume 'all' (adjust if needed)
    return 'all';
};

// Removed the outdated updatePermsFromSelect helper function

// --- Component State and Logic ---
const PermissionsSettings = () => {
  const [allRoles, setAllRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [selectedRoleData, setSelectedRoleData] = useState(null);
  const [currentPermissions, setCurrentPermissions] = useState({});
  const [initialPermissions, setInitialPermissions] = useState({});

  const [loadingRoles, setLoadingRoles] = useState(true);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDescription, setNewRoleDescription] = useState('');
  const [editingRole, setEditingRole] = useState(null);

  const API_BASE_URL = '/api';

  const fetchRoles = useCallback(async () => {
    setLoadingRoles(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'x-auth-token': token } };
      const response = await axios.get(`${API_BASE_URL}/roles`, config);
      setAllRoles(response.data || []);
      if (response.data.length > 0 && (!selectedRoleId || !response.data.find(r => r.id === selectedRoleId))) {
        setSelectedRoleId(response.data[0].id);
      } else if (response.data.length === 0) {
        setSelectedRoleId(null);
        setSelectedRoleData(null);
        setCurrentPermissions({});
      }
    } catch (err) {
      console.error('Error fetching roles:', err);
      setError('Failed to load roles.');
      toast.error('Failed to load roles.');
    } finally {
      setLoadingRoles(false);
    }
  }, [selectedRoleId]);

  const fetchPermissionsForRole = useCallback(async (roleId) => {
    if (!roleId) {
      setCurrentPermissions({});
      setInitialPermissions({});
      setSelectedRoleData(null);
      return;
    }
    setLoadingPermissions(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'x-auth-token': token } };
      const response = await axios.get(`${API_BASE_URL}/roles/${roleId}/permissions`, config);
      setCurrentPermissions(response.data || {});
      setInitialPermissions(JSON.parse(JSON.stringify(response.data || {})));
      const roleData = allRoles.find(r => r.id === roleId);
      setSelectedRoleData(roleData || null);
    } catch (err) {
      console.error(`Error fetching permissions for role ${roleId}:`, err);
      setError(`Failed to load permissions for role ${selectedRoleData?.name || roleId}.`);
      toast.error(`Failed to load permissions for role ${selectedRoleData?.name || roleId}.`);
      setCurrentPermissions({});
      setInitialPermissions({});
    } finally {
      setLoadingPermissions(false);
    }
  }, [allRoles, selectedRoleData?.name]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  useEffect(() => {
    fetchPermissionsForRole(selectedRoleId);
  }, [selectedRoleId, fetchPermissionsForRole]);

  const handlePermissionChange = (category, key, type, value) => {
    setCurrentPermissions(prev => {
      const newPerms = JSON.parse(JSON.stringify(prev));
      if (!newPerms[category]) {
        newPerms[category] = {};
      }

      // Simplified logic: directly set enabled/value based on type
      if (type === 'boolean') {
        newPerms[category][key] = { enabled: value };
      } else if (type === 'select') {
        // For ALL select types (read, update, delete, export, complete, segments_access)
        // Set enabled based on whether the value is 'none'
        // Set the value directly
        newPerms[category][key] = { enabled: value !== 'none', value: value };
      }

      if (key === 'access' && type === 'boolean' && !value) {
          Object.keys(PERMISSION_SCHEMA[category]?.permissions || {}).forEach(childKey => {
              if (childKey !== 'access') {
                  newPerms[category][childKey] = { ...(newPerms[category][childKey] || {}), enabled: false };
              }
          });
      }

      return newPerms;
    });
  };

  const savePermissions = async () => {
    if (!selectedRoleId || selectedRoleData?.name === 'superuser') {
      toast.warn('Cannot modify superuser permissions.');
      return;
    }
    setIsSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'x-auth-token': token } };
      await axios.put(`${API_BASE_URL}/roles/${selectedRoleId}/permissions`, { permissions: currentPermissions }, config);
      toast.success(`Permissions for role "${selectedRoleData?.name}" saved successfully.`);
      setInitialPermissions(JSON.parse(JSON.stringify(currentPermissions)));
    } catch (err) {
      console.error(`Error saving permissions for role ${selectedRoleId}:`, err);
      const errorMsg = err.response?.data?.message || 'Failed to save permissions.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const openCreateModal = () => {
    setNewRoleName('');
    setNewRoleDescription('');
    setIsCreateModalOpen(true);
  };

  const handleCreateRole = async (e) => {
    e.preventDefault();
    if (!newRoleName.trim()) {
      toast.warn('Role name cannot be empty.');
      return;
    }
    setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'x-auth-token': token } };
      const response = await axios.post(`${API_BASE_URL}/roles`, { name: newRoleName, description: newRoleDescription }, config);
      toast.success(`Role "${response.data.name}" created successfully.`);
      setIsCreateModalOpen(false);
      await fetchRoles();
      setSelectedRoleId(response.data.id);
    } catch (err) {
      console.error('Error creating role:', err);
      const errorMsg = err.response?.data?.message || 'Failed to create role.';
      toast.error(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const openEditModal = (role) => {
    if (role.is_system_role) {
      toast.warn('System roles cannot be edited.');
      return;
    }
    setEditingRole(role);
    setNewRoleName(role.name);
    setNewRoleDescription(role.description || '');
    setIsEditModalOpen(true);
  };

  const handleEditRole = async (e) => {
    e.preventDefault();
    if (!editingRole || !newRoleName.trim()) {
      toast.warn('Role name cannot be empty.');
      return;
    }
    setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'x-auth-token': token } };
      const response = await axios.put(`${API_BASE_URL}/roles/${editingRole.id}`, { name: newRoleName, description: newRoleDescription }, config);
      toast.success(`Role "${response.data.name}" updated successfully.`);
      setIsEditModalOpen(false);
      setEditingRole(null);
      await fetchRoles();
      if (selectedRoleId === response.data.id) {
          setSelectedRoleData(response.data);
      }
    } catch (err) {
      console.error('Error updating role:', err);
      const errorMsg = err.response?.data?.message || 'Failed to update role.';
      toast.error(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteRole = async (role) => {
    if (role.is_system_role) {
      toast.warn('System roles cannot be deleted.');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete the role "${role.name}"? This action cannot be undone.`)) {
      return;
    }
    setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'x-auth-token': token } };
      await axios.delete(`${API_BASE_URL}/roles/${role.id}`, config);
      toast.success(`Role "${role.name}" deleted successfully.`);
      if (selectedRoleId === role.id) {
          setSelectedRoleId(null);
      }
      await fetchRoles();
    } catch (err) {
      console.error('Error deleting role:', err);
      const errorMsg = err.response?.data?.message || 'Failed to delete role.';
      toast.error(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = JSON.stringify(currentPermissions) !== JSON.stringify(initialPermissions);

  // --- Render Helper using standard HTML ---
  const renderPermissionInput = (category, key, config) => {
    const currentCategoryPermissions = currentPermissions[category] || {};
    // Check if category access exists and is disabled. If access perm doesn't exist, assume enabled.
    const isCategoryAccessDisabled = PERMISSION_SCHEMA[category]?.permissions?.access && !(currentCategoryPermissions.access?.enabled ?? true);
    const isDisabled = selectedRoleData?.name === 'superuser' || (key !== 'access' && isCategoryAccessDisabled);
    const inputId = `${category}-${key}`;

    if (config.type === 'boolean') {
      return (
        <div key={inputId} className="flex items-center">
          <input
            type="checkbox"
            id={inputId}
            checked={currentCategoryPermissions[key]?.enabled || false}
            onChange={(e) => handlePermissionChange(category, key, 'boolean', e.target.checked)}
            disabled={isDisabled}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
          />
          <label htmlFor={inputId} className={`ml-2 block text-sm ${isDisabled ? 'text-gray-400' : 'text-gray-900'}`}>
            {config.label}
          </label>
        </div>
      );
    } else if (config.type === 'select') {
      // Use the updated getSelectValue which checks the 'value' property first
      const selectValue = getSelectValue(currentCategoryPermissions, key);

      return (
        <div key={inputId}>
          <label htmlFor={inputId} className={`block text-sm font-medium ${isDisabled ? 'text-gray-400' : 'text-gray-700'} mb-1`}>
            {config.label}
          </label>
          <select
            id={inputId}
            value={selectValue}
            onChange={(e) => handlePermissionChange(category, key, 'select', e.target.value)}
            disabled={isDisabled}
            className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:opacity-50 disabled:bg-gray-100"
          >
            {config.options.map(option => (
              <option key={option} value={option}>
                {option.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </option>
            ))}
          </select>
           {key === 'segments_access' && (
             <p className="mt-1 text-xs text-gray-500">
               {(() => {
                 switch(selectValue) {
                   case 'view_only': return 'Can view saved segments.';
                   case 'create_delete_own': return 'Can create/delete own private segments.';
                   case 'create_for_all': return 'Can create public segments, delete own.';
                   case 'full_access': return 'Can create/delete all segments.';
                   default: return '';
                 }
               })()}
             </p>
           )}
        </div>
      );
    }
    return null;
  };

  const isSuperUser = selectedRoleData?.name === 'superuser'; // Define isSuperUser here

  // --- JSX using standard HTML elements with Tailwind, inspired by old layout ---
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900">Role & Permissions Management</h3>
        <Button onClick={openCreateModal} variant="primary" size="sm" disabled={loadingRoles}>
          Create New Role
        </Button>
      </div>
      <p className="text-gray-600 mb-6">Define roles and manage their permissions within the CRM.</p>

      {error && <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">{error}</div>}

      {loadingRoles ? (
        <p>Loading roles...</p>
      ) : (
        <>
          {/* Role selector tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8 overflow-x-auto" aria-label="Tabs">
              {allRoles.map((role) => (
                <button
                  key={role.id}
                  onClick={() => setSelectedRoleId(role.id)}
                  className={`flex-shrink-0 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                    selectedRoleId === role.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {role.name} {role.is_system_role && '(System)'}
                </button>
              ))}
            </nav>
          </div>

          {/* Permission grid for selected role */}
          {selectedRoleId && selectedRoleData ? (
            <div className="bg-white rounded-lg border border-gray-200">
              {/* Role Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-start">
                 <div>
                  <h4 className="text-base font-medium text-gray-900">{selectedRoleData.name}</h4>
                  <p className="text-sm text-gray-500 mt-1">{selectedRoleData.description || 'No description provided.'}</p>
                  {selectedRoleData.is_system_role && (
                     <span className="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                       System Role
                     </span>
                  )}
                 </div>
                 {!selectedRoleData.is_system_role && (
                    <div className="flex-shrink-0 flex space-x-2">
                         <Button onClick={() => openEditModal(selectedRoleData)} variant="outline" size="sm">Edit Role</Button>
                         <Button onClick={() => handleDeleteRole(selectedRoleData)} variant="danger" size="sm" disabled={isSaving}>Delete Role</Button>
                    </div>
                 )}
              </div>

              {/* Permissions Loading State */}
              {loadingPermissions ? (
                <p className="p-6 text-center">Loading permissions...</p>
              ) : (
                <>
                  {/* Permission Categories */}
                  <div className="divide-y divide-gray-200">
                    {Object.entries(PERMISSION_SCHEMA).map(([category, categoryConfig]) => {
                      const currentCategoryPermissions = currentPermissions[category] || {};
                      const hasAccessPermission = !!categoryConfig.permissions.access;
                      const isAccessEnabled = hasAccessPermission ? (currentCategoryPermissions.access?.enabled ?? false) : true; // Assume enabled if no access toggle exists
                      // Removed isSuperUser definition from here

                      return (
                        <div key={category} className="px-6 py-4">
                          <h5 className="text-sm font-medium text-gray-700 mb-3">{categoryConfig.label}</h5>

                          {/* Enable Access Checkbox (if applicable) */}
                          {hasAccessPermission && (
                            <div className="mb-4 flex items-center py-2 px-3 bg-blue-50 rounded-md border border-blue-100">
                              <input
                                type="checkbox"
                                id={`${category}-access`}
                                checked={isAccessEnabled}
                                onChange={(e) => handlePermissionChange(category, 'access', 'boolean', e.target.checked)}
                                disabled={isSuperUser}
                                className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                              />
                              <label htmlFor={`${category}-access`} className={`ml-2 block text-sm font-medium ${isSuperUser ? 'text-gray-400' : 'text-blue-700'}`}>
                                {categoryConfig.permissions.access.label}
                              </label>
                            </div>
                          )}

                          {/* Granular Permissions (if access is enabled or no access toggle exists) */}
                          {(isAccessEnabled || !hasAccessPermission) && (
                            <div className={`space-y-6 ${isSuperUser ? 'opacity-70' : ''}`}>
                              {/* Group Select/Dropdown Permissions */}
                              {Object.entries(categoryConfig.permissions).some(([key, config]) => key !== 'access' && config.type === 'select') && (
                                <div>
                                  <h6 className="text-sm font-medium text-gray-700 mb-3">Access Controls</h6>
                                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                    {Object.entries(categoryConfig.permissions)
                                      .filter(([key, config]) => key !== 'access' && config.type === 'select')
                                      .map(([key, config]) => renderPermissionInput(category, key, config))
                                    }
                                  </div>
                                </div>
                              )}

                              {/* Group Boolean/Checkbox Permissions */}
                              {Object.entries(categoryConfig.permissions).some(([key, config]) => key !== 'access' && config.type === 'boolean') && (
                                <div>
                                  <h6 className="text-sm font-medium text-gray-700 mb-3">Additional Permissions</h6>
                                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                    {Object.entries(categoryConfig.permissions)
                                      .filter(([key, config]) => key !== 'access' && config.type === 'boolean')
                                      .map(([key, config]) => renderPermissionInput(category, key, config))
                                    }
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Footer/Save Button */}
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
                    {isSuperUser ? (
                       <p className="text-sm text-gray-600 italic">Superuser permissions cannot be changed.</p>
                    ) : (
                       <Button onClick={savePermissions} variant="primary" disabled={isSaving || !hasChanges} isLoading={isSaving}>
                         {isSaving ? 'Saving...' : 'Save Permissions'}
                       </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          ) : (
             !loadingRoles && <p className="text-center text-gray-500 py-6">Select a role to view or manage its permissions.</p>
          )}
        </>
      )}

      {/* --- Modals remain unchanged --- */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <form onSubmit={handleCreateRole}>
              <div className="p-6 border-b">
                <h3 className="text-lg font-semibold">Create New Role</h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label htmlFor="newRoleName" className="block text-sm font-medium text-gray-700 mb-1">Role Name *</label>
                  <input
                    type="text"
                    id="newRoleName"
                    value={newRoleName}
                    onChange={(e) => setNewRoleName(e.target.value)}
                    placeholder="e.g., Marketing Manager"
                    required
                    className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="newRoleDesc" className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
                  <textarea
                    id="newRoleDesc"
                    value={newRoleDescription}
                    onChange={(e) => setNewRoleDescription(e.target.value)}
                    placeholder="Briefly describe the purpose of this role"
                    rows={3}
                    className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>
              <div className="bg-gray-50 px-6 py-3 flex justify-end space-x-3">
                <Button type="button" variant="outline" onClick={() => setIsCreateModalOpen(false)}>Cancel</Button>
                <Button type="submit" variant="primary" disabled={isSaving} isLoading={isSaving}>Create Role</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isEditModalOpen && editingRole && (
         <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <form onSubmit={handleEditRole}>
               <div className="p-6 border-b">
                 <h3 className="text-lg font-semibold">Edit Role: {editingRole.name}</h3>
               </div>
              <div className="p-6 space-y-4">
                 <div>
                  <label htmlFor="editRoleName" className="block text-sm font-medium text-gray-700 mb-1">Role Name *</label>
                  <input
                    type="text"
                    id="editRoleName"
                    value={newRoleName}
                    onChange={(e) => setNewRoleName(e.target.value)}
                    required
                    className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="editRoleDesc" className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
                  <textarea
                    id="editRoleDesc"
                    value={newRoleDescription}
                    onChange={(e) => setNewRoleDescription(e.target.value)}
                    rows={3}
                     className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>
              <div className="bg-gray-50 px-6 py-3 flex justify-end space-x-3">
                <Button type="button" variant="outline" onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
                <Button type="submit" variant="primary" disabled={isSaving} isLoading={isSaving}>Save Changes</Button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default PermissionsSettings;
